import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Copy, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';
import {
  hrCreateJob,
  hrGetJob,
  hrPatchJob,
  hrCreateTask,
  hrPatchTask,
  hrDeleteTask,
  hrGenerateTasks,
  hrPublishJob,
} from '../../lib/jobsApi';
import type { HrJob, HrTask, SubmissionType } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { usePolling } from '../../lib/usePolling';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';

/*
 * Velaigal /hr/create-job (and /hr/jobs/:id/edit) — the two-step job wizard.
 *
 * Step A (job details) creates or patches the job row itself; Step B (tasks)
 * is only reachable once a job exists, and is where AI generation, manual
 * task CRUD and publishing happen. The two steps are just local state
 * (`wizardStep`) on top of one `job` record — there is no URL change on
 * create, so a mid-wizard refresh drops back to a blank Step A (the draft
 * itself is safe on the server; it's reachable again later via
 * /hr/jobs/:id/edit, which is how the Jobs list is expected to link back in).
 *
 * Loading by route param status:
 *   no :id            -> fresh create, Step A, job = null
 *   :id, status draft -> load straight into Step B
 *   :id, status published -> metadata-only edit form + locked tasks list
 *   :id, status closed -> fully read-only view
 * "Edit job details" in Step B flips back to Step A for an existing draft,
 * reusing the exact same form (hrPatchJob instead of hrCreateJob).
 *
 * Task generation polls hrGetJob every 2.5s via the same usePolling hook
 * Resume.tsx uses, while taskGenStatus is queued/running, and stops itself
 * (both by the poll fn returning false and by the conditional unmount) once
 * it lands on idle (done) or failed.
 *
 * Every sub-component below is module-scope — the ones that repeat per task
 * (TaskRow, TaskForm) carry their own local edit/busy state, which is what
 * gives per-task edit state naturally keyed by task id: each row is its own
 * component instance keyed by task.id in the list below.
 */

const MAX_TASKS = 5;
const MAX_CRITERIA = 20;

const ALL_SUBMISSION_TYPES: SubmissionType[] = ['hosted_link', 'repo', 'video', 'document'];
const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  hosted_link: 'Hosted link',
  repo: 'Repository',
  video: 'Video',
  document: 'Document',
};

function clampNumber(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function originLabel(task: HrTask): string {
  if (task.origin === 'manual') return 'Manual';
  return task.editedAt ? 'AI · edited' : 'AI draft';
}

function copyPublicLink(link: string): void {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(link).then(
      () => toast.success('Public link copied.'),
      () => toast.error('Could not copy the link.'),
    );
  } else {
    toast.error('Could not copy — copy it manually from the address shown.');
  }
}

function showPublishedToast(link: string): void {
  toast.custom(
    (t: { id: string }) => (
      <div className="card" style={{ maxWidth: 380 }} role="status">
        <p className="text-sm font-medium text-ink mb-1">Job published.</p>
        <p className="text-xs text-ink-tertiary break-all mb-3">{link}</p>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn-secondary"
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
            onClick={() => {
              copyPublicLink(link);
              toast.dismiss(t.id);
            }}
          >
            <Copy className="h-3 w-3" strokeWidth={1.75} />
            Copy public link
          </button>
          <button
            type="button"
            className="btn-secondary"
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
            onClick={() => toast.dismiss(t.id)}
          >
            Dismiss
          </button>
        </div>
      </div>
    ),
    { duration: 10000 },
  );
}

const InlineSpinner: React.FC<{ label: string; variant?: 'light' | 'dark' }> = ({ label, variant = 'light' }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={
        variant === 'light'
          ? { borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }
          : { borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }
      }
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this job.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This job isn&apos;t available.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      It may have been deleted, or you don&apos;t have access to it.
    </p>
    <Link to="/hr/jobs" className="btn-primary">
      Back to your jobs
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Couldn&apos;t load this job.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

interface JobDetailsValues {
  title: string;
  description: string;
  experienceYearsMin: number;
  education: string;
  positionLevel: string;
  salaryDisplay: string;
}

function valuesFromJob(job: HrJob | null): JobDetailsValues {
  return {
    title: job?.title ?? '',
    description: job?.description ?? '',
    experienceYearsMin: job?.experienceYearsMin ?? 0,
    education: job?.education ?? '',
    positionLevel: job?.positionLevel ?? '',
    salaryDisplay: job?.salaryDisplay ?? '',
  };
}

interface StepDetailsFormProps {
  job: HrJob | null;
  submitLabel: string;
  onSaved: (job: HrJob) => void;
  onCancel?: () => void;
}

const StepDetailsForm: React.FC<StepDetailsFormProps> = ({ job, submitLabel, onSaved, onCancel }) => {
  const [values, setValues] = useState<JobDetailsValues>(() => valuesFromJob(job));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const setField = <K extends keyof JobDetailsValues>(key: K, value: JobDetailsValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) setErrors((prev) => ({ ...prev, [key as string]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;

    const nextErrors: Record<string, string> = {};
    if (!values.title.trim()) nextErrors.title = 'Title is required.';
    if (!Number.isInteger(values.experienceYearsMin) || values.experienceYearsMin < 0 || values.experienceYearsMin > 50) {
      nextErrors.experienceYearsMin = 'Enter a whole number between 0 and 50.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setBusy(true);
    const body = {
      title: values.title.trim(),
      description: values.description.trim(),
      experienceYearsMin: values.experienceYearsMin,
      education: values.education.trim(),
      positionLevel: values.positionLevel.trim(),
      salaryDisplay: values.salaryDisplay.trim() ? values.salaryDisplay.trim() : null,
    };
    try {
      const result = job ? await hrPatchJob(job.id, body) : await hrCreateJob(body);
      // The server always returns the full row on both routes — the `{ ok: true }`
      // half of hrPatchJob's return type is dead in practice (see hrJobs.ts).
      onSaved((result as { job: HrJob }).job);
    } catch (err) {
      if (err instanceof ApiError && err.fields) {
        setErrors(err.fields);
      } else {
        toast.error(hrErrorMessage(err, 'Could not save this job.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="space-y-2">
        <label htmlFor="job-title" className="block text-sm font-medium text-ink">
          Job title
        </label>
        <input
          id="job-title"
          type="text"
          className="input"
          maxLength={200}
          value={values.title}
          onChange={(e) => setField('title', e.target.value)}
          aria-invalid={!!errors.title}
          style={errors.title ? { borderColor: 'var(--color-error)' } : undefined}
        />
        {errors.title && (
          <p role="alert" className="text-xs text-error font-medium">
            {errors.title}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="job-description" className="block text-sm font-medium text-ink">
          Description
        </label>
        <textarea
          id="job-description"
          className="input resize-none"
          rows={8}
          maxLength={20000}
          value={values.description}
          onChange={(e) => setField('description', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="job-experience" className="block text-sm font-medium text-ink">
          Minimum years of experience
        </label>
        <input
          id="job-experience"
          type="number"
          className="input"
          min={0}
          max={50}
          value={values.experienceYearsMin}
          onChange={(e) => setField('experienceYearsMin', clampNumber(Math.round(Number(e.target.value)), 0, 50))}
          aria-invalid={!!errors.experienceYearsMin}
          style={errors.experienceYearsMin ? { borderColor: 'var(--color-error)' } : undefined}
        />
        {errors.experienceYearsMin && (
          <p role="alert" className="text-xs text-error font-medium">
            {errors.experienceYearsMin}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="job-education" className="block text-sm font-medium text-ink">
          Education
        </label>
        <input
          id="job-education"
          type="text"
          className="input"
          maxLength={300}
          placeholder="e.g., Bachelor's in Computer Science"
          value={values.education}
          onChange={(e) => setField('education', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="job-level" className="block text-sm font-medium text-ink">
          Position level
        </label>
        <input
          id="job-level"
          type="text"
          className="input"
          maxLength={100}
          placeholder="e.g., Mid-level"
          value={values.positionLevel}
          onChange={(e) => setField('positionLevel', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="job-salary" className="block text-sm font-medium text-ink">
          Salary
        </label>
        <input
          id="job-salary"
          type="text"
          className="input"
          maxLength={100}
          placeholder="e.g., $80,000 – $100,000"
          value={values.salaryDisplay}
          onChange={(e) => setField('salaryDisplay', e.target.value)}
        />
        <p className="text-xs text-ink-tertiary">Leave blank to hide.</p>
      </div>

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? <InlineSpinner label="Saving…" /> : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

interface TaskFormProps {
  jobId: string;
  initial: HrTask | null;
  onSaved: (task: HrTask) => void;
  onCancel: () => void;
}

interface CriterionRow {
  id: number;
  criterion: string;
  weight: number;
}

const TaskForm: React.FC<TaskFormProps> = ({ jobId, initial, onSaved, onCancel }) => {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [brief, setBrief] = useState(initial?.brief ?? '');
  const [types, setTypes] = useState<SubmissionType[]>(initial?.allowedSubmissionTypes ?? []);

  // Stable per-row ids, independent of array position, so React keeps each
  // criterion's DOM (and any focused input) attached to the right row when a
  // row in the middle is removed — index keys would otherwise reshuffle.
  const nextCriterionId = useRef(0);
  const makeCriterionRow = (criterion = '', weight = 5): CriterionRow => ({
    id: nextCriterionId.current++,
    criterion,
    weight,
  });
  const [criteria, setCriteria] = useState<CriterionRow[]>(() => {
    const source = initial && initial.evaluationCriteria.length > 0 ? initial.evaluationCriteria : null;
    return source ? source.map((c) => makeCriterionRow(c.criterion, c.weight)) : [makeCriterionRow()];
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldSuffix = initial?.id ?? 'new';

  const toggleType = (t: SubmissionType) => {
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const updateCriterion = (id: number, patch: Partial<{ criterion: string; weight: number }>) => {
    setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const removeCriterion = (id: number) => {
    setCriteria((prev) => prev.filter((c) => c.id !== id));
  };

  const addCriterion = () => {
    setCriteria((prev) => (prev.length >= MAX_CRITERIA ? prev : [...prev, makeCriterionRow()]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!brief.trim()) {
      setError('Brief is required.');
      return;
    }
    if (types.length === 0) {
      setError('Select at least one submission format.');
      return;
    }

    setBusy(true);
    setError(null);
    const body = {
      title: title.trim(),
      brief: brief.trim(),
      allowedSubmissionTypes: types,
      evaluationCriteria: criteria
        .filter((c) => c.criterion.trim() !== '')
        .map((c) => ({ criterion: c.criterion.trim(), weight: c.weight })),
    };
    try {
      const result = initial ? await hrPatchTask(jobId, initial.id, body) : await hrCreateTask(jobId, body);
      // The server always returns the full row on both routes — the `{ ok: true }`
      // half of hrPatchTask's return type is dead in practice (see hrJobs.ts).
      onSaved((result as { task: HrTask }).task);
    } catch (err) {
      setError(hrErrorMessage(err, 'Could not save this task.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="mt-5 pt-5 border-t border-hairline space-y-5">
      {error && (
        <p role="alert" className="text-xs text-error font-medium">
          {error}
        </p>
      )}

      <div className="space-y-2">
        <label htmlFor={`task-title-${fieldSuffix}`} className="block text-sm font-medium text-ink">
          Task title
        </label>
        <input
          id={`task-title-${fieldSuffix}`}
          type="text"
          className="input"
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor={`task-brief-${fieldSuffix}`} className="block text-sm font-medium text-ink">
          Brief
        </label>
        <textarea
          id={`task-brief-${fieldSuffix}`}
          className="input resize-none"
          rows={5}
          maxLength={20000}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <span className="block text-sm font-medium text-ink">Allowed submission formats</span>
        <div className="flex flex-wrap gap-4">
          {ALL_SUBMISSION_TYPES.map((t) => (
            <label key={t} className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
              <input
                type="checkbox"
                checked={types.includes(t)}
                onChange={() => toggleType(t)}
                className="h-4 w-4"
                style={{ accentColor: 'var(--color-ink)' }}
              />
              {SUBMISSION_TYPE_LABELS[t]}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <span className="label-editorial block">Private scoring rubric — candidates never see this</span>
        <div className="space-y-2">
          {criteria.map((c) => (
            <div key={c.id} className="flex items-center gap-2">
              <input
                type="text"
                className="input"
                placeholder="Criterion"
                maxLength={500}
                value={c.criterion}
                onChange={(e) => updateCriterion(c.id, { criterion: e.target.value })}
              />
              <input
                type="number"
                className="input"
                style={{ width: '5rem', flexShrink: 0 }}
                min={0}
                max={10}
                value={c.weight}
                onChange={(e) => updateCriterion(c.id, { weight: clampNumber(Number(e.target.value), 0, 10) })}
                aria-label="Weight, 0 to 10"
              />
              <button
                type="button"
                onClick={() => removeCriterion(c.id)}
                className="text-ink-tertiary hover:text-error transition-colors flex-shrink-0"
                aria-label="Remove criterion"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary"
          style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
          onClick={addCriterion}
          disabled={criteria.length >= MAX_CRITERIA}
        >
          <Plus className="h-3 w-3" strokeWidth={1.75} />
          Add criterion
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? <InlineSpinner label="Saving…" /> : initial ? 'Save changes' : 'Add task'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
};

interface TaskRowProps {
  jobId: string;
  index: number;
  task: HrTask;
  onSaved: (task: HrTask) => void;
  onDeleted: (taskId: string) => void;
}

const TaskRow: React.FC<TaskRowProps> = ({ jobId, index, task, onSaved, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSaved = (saved: HrTask) => {
    setExpanded(false);
    onSaved(saved);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await hrDeleteTask(jobId, task.id);
      onDeleted(task.id);
    } catch (err) {
      toast.error(hrErrorMessage(err, 'Could not delete this task.'));
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <span className="label-editorial block mb-1" style={{ color: 'var(--color-saffron)' }}>
            Task {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="font-display text-lg font-medium text-ink">{task.title}</h3>
          {!expanded && (
            <p className="text-sm text-ink-secondary mt-2 leading-relaxed">
              {task.brief.length > 160 ? `${task.brief.slice(0, 160)}…` : task.brief}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span
              className="chip"
              style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
            >
              {originLabel(task)}
            </span>
            {task.allowedSubmissionTypes.map((t) => (
              <span key={t} className="chip">
                {SUBMISSION_TYPE_LABELS[t]}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn-secondary"
            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
            onClick={() => setExpanded((v) => !v)}
          >
            <Pencil className="h-3 w-3" strokeWidth={1.75} />
            {expanded ? 'Close' : 'Edit'}
          </button>
          {confirmingDelete ? (
            <>
              <button
                type="button"
                className="btn-danger"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? <InlineSpinner label="Deleting…" /> : 'Confirm delete'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="text-ink-tertiary hover:text-error transition-colors"
              aria-label={`Delete ${task.title}`}
              onClick={() => setConfirmingDelete(true)}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <TaskForm jobId={jobId} initial={task} onSaved={handleSaved} onCancel={() => setExpanded(false)} />
      )}
    </article>
  );
};

const ReadOnlyTaskCard: React.FC<{ task: HrTask; index: number }> = ({ task, index }) => (
  <article className="card">
    <span className="label-editorial block mb-1" style={{ color: 'var(--color-saffron)' }}>
      Task {String(index + 1).padStart(2, '0')}
    </span>
    <h3 className="font-display text-lg font-medium text-ink">{task.title}</h3>
    <p className="text-sm text-ink-secondary mt-2 leading-relaxed whitespace-pre-line">{task.brief}</p>
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <span className="chip" style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}>
        {originLabel(task)}
      </span>
      {task.allowedSubmissionTypes.map((t) => (
        <span key={t} className="chip">
          {SUBMISSION_TYPE_LABELS[t]}
        </span>
      ))}
    </div>
    {task.evaluationCriteria.length > 0 && (
      <div className="mt-4 pt-4 border-t border-hairline">
        <span className="label-editorial block mb-2">Private scoring rubric — candidates never see this</span>
        <ul className="space-y-1">
          {task.evaluationCriteria.map((c, i) => (
            <li key={i} className="text-sm text-ink-secondary flex items-center justify-between gap-4">
              <span className="min-w-0 truncate">{c.criterion}</span>
              <span className="text-ink-tertiary flex-shrink-0">{c.weight}/10</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </article>
);

const ClosedJobView: React.FC<{ job: HrJob; tasks: HrTask[] }> = ({ job, tasks }) => (
  <div className="space-y-8">
    <div role="status" className="bg-paper border border-hairline rounded-md p-4">
      <p className="text-sm text-ink-secondary">This job is closed and can no longer be edited.</p>
    </div>
    <div className="card space-y-4">
      <div>
        <span className="label-editorial block mb-1">Description</span>
        <p className="text-sm text-ink-secondary whitespace-pre-line">{job.description || '—'}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="label-editorial block mb-1">Experience</span>
          <p className="text-ink">{job.experienceYearsMin}+ years</p>
        </div>
        <div>
          <span className="label-editorial block mb-1">Education</span>
          <p className="text-ink">{job.education || '—'}</p>
        </div>
        <div>
          <span className="label-editorial block mb-1">Level</span>
          <p className="text-ink">{job.positionLevel || '—'}</p>
        </div>
        <div>
          <span className="label-editorial block mb-1">Salary</span>
          <p className="text-ink">{job.salaryDisplay || 'Hidden'}</p>
        </div>
      </div>
    </div>
    <div>
      <span className="label-editorial block mb-4">Tasks ({tasks.length})</span>
      <div className="space-y-4">
        {tasks.map((task, i) => (
          <ReadOnlyTaskCard key={task.id} task={task} index={i} />
        ))}
      </div>
    </div>
  </div>
);

const TaskGenPoller: React.FC<{ onTick: () => Promise<boolean> }> = ({ onTick }) => {
  usePolling(onTick, 2500);
  return null;
};

interface GenerationStatusProps {
  job: HrJob;
  generating: boolean;
  publishing: boolean;
  /* Tasks a regenerate will NOT delete: manual, plus AI tasks the recruiter edited. */
  keptCount: number;
  onGenerate: () => void;
}

const MIN_AI_TASKS = 2;

const GenerationStatus: React.FC<GenerationStatusProps> = ({ job, generating, publishing, keptCount, onGenerate }) => {
  if (job.taskGenStatus === 'queued' || job.taskGenStatus === 'running') {
    return (
      <div role="status" className="flex items-center gap-3 px-4 py-3 bg-paper border border-hairline rounded-md">
        <span aria-hidden="true" className="velaigal-loader-rule w-12 flex-shrink-0" />
        <p className="text-sm text-ink-secondary">Drafting tasks from your JD — up to a minute…</p>
      </div>
    );
  }

  if (job.taskGenStatus === 'failed') {
    return (
      <div role="alert" className="px-4 py-4 bg-paper border border-warning rounded space-y-3">
        <p className="text-sm text-ink">{job.taskGenError || 'Task generation failed.'}</p>
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" className="btn-secondary" onClick={onGenerate} disabled={generating || publishing}>
            {generating ? <InlineSpinner label="Retrying…" variant="dark" /> : 'Try again'}
          </button>
          <span className="text-xs text-ink-tertiary">or write tasks manually</span>
        </div>
      </div>
    );
  }

  // The server refuses with 409 here; saying so up front beats offering the button and then
  // explaining the failure.
  if (MAX_TASKS - keptCount < MIN_AI_TASKS) {
    return (
      <div className="px-4 py-4 bg-paper border border-hairline rounded space-y-2">
        <p className="text-sm text-ink">
          You have {keptCount} tasks generation won&apos;t replace — remove at least{' '}
          {MIN_AI_TASKS - (MAX_TASKS - keptCount)} before generating.
        </p>
        <p className="text-xs text-ink-tertiary">
          A job can have at most {MAX_TASKS} tasks, and the AI drafts {MIN_AI_TASKS} to 3 at a time.
        </p>
      </div>
    );
  }

  return (
    <button type="button" className="btn-primary" onClick={onGenerate} disabled={generating || publishing}>
      {generating ? (
        <InlineSpinner label="Starting…" />
      ) : (
        <>
          <Sparkles className="h-4 w-4" strokeWidth={1.75} />
          Generate with AI
        </>
      )}
    </button>
  );
};

const CreateJob: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(!!id);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [job, setJob] = useState<HrJob | null>(null);
  const [tasks, setTasks] = useState<HrTask[]>([]);
  const [wizardStep, setWizardStep] = useState<'details' | 'tasks'>('details');

  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [addingTask, setAddingTask] = useState(false);

  // Load-by-id — cancelled-guarded so a fast unmount (or a stale in-flight
  // request from a previous id/retry) can't setState afterward.
  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await hrGetJob(id);
        if (cancelled) return;
        setJob(res.job);
        setTasks(res.tasks);
        setWizardStep(res.job.status === 'draft' ? 'tasks' : 'details');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  const pollTick = useCallback(async (): Promise<boolean> => {
    const jobId = job?.id;
    if (!jobId) return false;
    try {
      const res = await hrGetJob(jobId);
      setJob(res.job);
      setTasks(res.tasks);
      return res.job.taskGenStatus === 'queued' || res.job.taskGenStatus === 'running';
    } catch {
      // Transient network hiccup — stay quiet and keep polling rather than
      // toasting on every failed background tick (mirrors Resume.tsx).
      return true;
    }
  }, [job?.id]);

  const handleDraftDetailsSaved = useCallback((savedJob: HrJob) => {
    // Optimistic update so Step B renders immediately with what the PATCH/POST
    // just returned...
    setJob(savedJob);
    setWizardStep('tasks');
    // ...then reconcile from the server. Step A can be reached from Step B via
    // "Edit job details" while a generation run is in flight (belt-and-suspenders:
    // that link is also disabled during generation, see below), and the PATCH
    // response only carries job fields — never tasks. If generation finished
    // while the user was on Step A, trusting only the PATCH payload would leave
    // the task list stale until a manual reload, so re-fetch the full job+tasks
    // here regardless.
    void (async () => {
      try {
        const res = await hrGetJob(savedJob.id);
        setJob(res.job);
        setTasks(res.tasks);
      } catch {
        // Keep the optimistic job/existing tasks — a failed background
        // reconcile shouldn't block the user from continuing the wizard.
      }
    })();
  }, []);

  const handlePublishedDetailsSaved = useCallback((savedJob: HrJob) => {
    setJob(savedJob);
    toast.success('Job details updated.');
  }, []);

  const handleTaskSaved = useCallback((task: HrTask) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
  }, []);

  const handleTaskCreated = useCallback((task: HrTask) => {
    setTasks((prev) => [...prev, task]);
    setAddingTask(false);
  }, []);

  const handleTaskDeleted = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const handleGenerate = async () => {
    if (!job || generating) return;
    setGenerating(true);
    setProfileIncomplete(false);
    try {
      await hrGenerateTasks(job.id);
      setJob((prev) => (prev ? { ...prev, taskGenStatus: 'queued', taskGenError: null } : prev));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'profile_incomplete') {
        setProfileIncomplete(true);
      } else {
        toast.error(hrErrorMessage(err, 'Could not start task generation.'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!job || publishing) return;
    setPublishing(true);
    setProfileIncomplete(false);
    try {
      await hrPublishJob(job.id);
      const link = `${window.location.origin}/jobs/${job.id}`;
      navigate('/hr/jobs');
      showPublishedToast(link);
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'profile_incomplete':
            setProfileIncomplete(true);
            break;
          case 'conflict':
            // The server returns 'conflict' both while task generation is
            // still running AND when the job is no longer a draft (e.g. a
            // stale tab republishing an already-published job) — only the
            // first case matches this hardcoded copy.
            if (job.taskGenStatus === 'queued' || job.taskGenStatus === 'running') {
              toast.error('Wait for task generation to finish');
            } else {
              toast.error(err.message);
            }
            break;
          case 'validation_failed':
          default:
            toast.error(err.message);
        }
      } else {
        toast.error('Check your connection and try again.');
      }
    } finally {
      setPublishing(false);
    }
  };

  if (id) {
    if (loading) return <LoadingNotice />;
    if (loadError?.notFound) return <NotFoundNotice />;
    if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
    if (!job) return null;
  }

  let content: React.ReactNode;

  if (job && job.status === 'closed') {
    content = <ClosedJobView job={job} tasks={tasks} />;
  } else if (job && job.status === 'published') {
    content = (
      <div className="space-y-8">
        <div className="bg-paper border border-hairline rounded-md p-4">
          <p className="text-sm text-ink-secondary">Tasks are locked after publish.</p>
        </div>
        <StepDetailsForm job={job} submitLabel="Save changes" onSaved={handlePublishedDetailsSaved} />
        <div>
          <span className="label-editorial block mb-4">Tasks ({tasks.length})</span>
          <div className="space-y-4">
            {tasks.map((task, i) => (
              <ReadOnlyTaskCard key={task.id} task={task} index={i} />
            ))}
          </div>
        </div>
      </div>
    );
  } else if (wizardStep === 'tasks' && job) {
    const genInProgress = job.taskGenStatus === 'queued' || job.taskGenStatus === 'running';
    content = (
      <div className="space-y-8">
        <div className="flex items-center justify-end">
          <button
            type="button"
            className="velaigal-link text-sm font-medium"
            onClick={() => setWizardStep('details')}
            disabled={genInProgress}
            title={genInProgress ? 'Wait for task generation to finish' : undefined}
            style={
              genInProgress
                ? { color: 'var(--color-ink-tertiary)', cursor: 'not-allowed', textDecoration: 'none' }
                : undefined
            }
          >
            Edit job details
          </button>
        </div>

        {profileIncomplete && (
          <div role="alert" className="flex items-start gap-2.5 px-4 py-3 border border-warning bg-paper text-sm text-ink rounded">
            <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0 mt-1.5" />
            <span>
              Complete your company profile before generating tasks or publishing.{' '}
              <Link to="/hr/profile" className="velaigal-link font-medium">
                Go to your profile
              </Link>
            </span>
          </div>
        )}

        <div>
          <span className="label-editorial block mb-3">Generate tasks</span>
          <GenerationStatus
            job={job}
            generating={generating}
            publishing={publishing}
            keptCount={tasks.filter((t) => t.origin === 'manual' || t.editedAt !== null).length}
            onGenerate={() => void handleGenerate()}
          />
        </div>

        <div>
          <span className="label-editorial block mb-4">
            Tasks ({tasks.length}/{MAX_TASKS})
          </span>
          {tasks.length === 0 && !addingTask && (
            <p className="text-sm text-ink-tertiary mb-4">No tasks yet — generate with AI or add one manually.</p>
          )}
          <div className="space-y-4">
            {tasks.map((task, i) => (
              <TaskRow key={task.id} jobId={job.id} index={i} task={task} onSaved={handleTaskSaved} onDeleted={handleTaskDeleted} />
            ))}
          </div>

          {addingTask ? (
            <div className="card mt-4">
              <span className="label-editorial block" style={{ color: 'var(--color-saffron)' }}>
                New task
              </span>
              <TaskForm jobId={job.id} initial={null} onSaved={handleTaskCreated} onCancel={() => setAddingTask(false)} />
            </div>
          ) : (
            tasks.length < MAX_TASKS && (
              <button type="button" className="btn-secondary mt-4" onClick={() => setAddingTask(true)}>
                <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
                Add task manually
              </button>
            )
          )}
        </div>

        <div className="pt-6 border-t border-hairline">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handlePublish()}
            disabled={tasks.length < 1 || tasks.length > MAX_TASKS || publishing || generating || genInProgress}
          >
            {publishing ? <InlineSpinner label="Publishing…" /> : 'Publish'}
          </button>
          {tasks.length < 1 && <p className="text-xs text-ink-tertiary mt-2">Add at least one task to publish.</p>}
          {tasks.length > MAX_TASKS && (
            <p className="text-xs text-ink-tertiary mt-2">A job can have at most {MAX_TASKS} tasks.</p>
          )}
        </div>

        {genInProgress && <TaskGenPoller onTick={pollTick} />}
      </div>
    );
  } else {
    content = (
      <StepDetailsForm
        job={job}
        submitLabel="Save and continue"
        onSaved={handleDraftDetailsSaved}
        onCancel={job ? () => setWizardStep('tasks') : undefined}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">
        {!job ? 'New job' : job.status === 'draft' ? 'Draft' : job.status === 'published' ? 'Published' : 'Closed'}
      </span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
        {job ? job.title : 'Create a job'}
      </h1>
      {content}
    </div>
  );
};

export default CreateJob;
